import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

/**
 * Standardized create / edit modal.
 *
 * Props:
 *  - open, onClose:  modal state
 *  - title, subtitle: header
 *  - error:          string | null — surfaced as an Alert above the form body
 *  - submitting:     boolean — disables submit + close, shows spinner on the submit button
 *  - onSubmit:       called when the user clicks submit; pass null to omit the submit button
 *  - submitLabel:    default "Save"
 *  - submitColor:    "primary" | "error" | etc. — defaults to primary
 *  - cancelLabel:    default "Cancel"
 *  - extraActions:   additional left-aligned actions in the footer
 *  - maxWidth:       MUI Dialog maxWidth — defaults to "sm"
 *  - fullWidth:      defaults to true
 *  - children:       the form body
 *
 * On mobile widths the dialog automatically goes full-screen for usability.
 */
export default function FormDialog({
  open,
  onClose,
  title,
  subtitle,
  error,
  submitting = false,
  onSubmit,
  submitLabel = "Save",
  submitColor = "primary",
  cancelLabel = "Cancel",
  extraActions,
  maxWidth = "sm",
  fullWidth = true,
  disableSubmit = false,
  children,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSubmit = (event) => {
    event?.preventDefault?.();
    if (onSubmit) onSubmit(event);
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={isMobile}
      PaperProps={{
        component: onSubmit ? "form" : "div",
        onSubmit: onSubmit ? handleSubmit : undefined,
        noValidate: true,
      }}
    >
      <DialogTitle component="div" sx={{ pr: 6 }}>
        <Stack spacing={0.25}>
          <Typography variant="subtitle1" fontWeight={700} component="span">
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              component="span"
            >
              {subtitle}
            </Typography>
          )}
        </Stack>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={submitting}
          size="small"
          sx={{ position: "absolute", top: 12, right: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ pt: 0.5 }}>{children}</Box>
      </DialogContent>
      <DialogActions
        sx={{
          flexDirection: { xs: "column-reverse", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        {extraActions && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              gap: 1,
              order: { xs: 2, sm: 0 },
            }}
          >
            {extraActions}
          </Box>
        )}
        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          spacing={1}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <Button
            onClick={onClose}
            disabled={submitting}
            variant="outlined"
            fullWidth={isMobile}
          >
            {cancelLabel}
          </Button>
          {onSubmit && (
            <Button
              type="submit"
              variant="contained"
              color={submitColor}
              disabled={submitting || disableSubmit}
              startIcon={
                submitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : null
              }
              fullWidth={isMobile}
            >
              {submitLabel}
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
